import { FC, ReactNode, useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cacheManager from '@/utils/cacheManager';
import PhantomLoader from '@/Components/Common/phantomLoader';
import { useAppDispatch } from '@/app/hooks';
import { fetchBaseQueryWithEncryption } from '@/features/fetchBaseQueryWithEncryption';
import { setCurrentUser, setRoleCodeDetails, setUserProfile } from '@/features/Global/slice';
import { roleAuthMgmt } from '@/features/RoleAuthMgmt/api';

const AUTH_CALLBACK_PATH = '/auth/callback';
const AUTH_SCOPE = 'email openid phone';
const TOKEN_KEYS = {
	ROLE: 'userRoleCode',
	USER_ID: 'userId',
} as const;

interface ProtectedRouteProps {
	children: ReactNode;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children }) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isLogin, setisLogin] = useState(false);

	const applyRolePermissions = (roleData: any) => {
		const updatedPermissions = roleData?.permissionList || [];
		dispatch(setRoleCodeDetails(updatedPermissions));
	};

	const buildOauthRedirect = useCallback(
		() => {
			const cognitoUrl = import.meta.env.VITE_COGNITO_AUTH_URL;
			const clientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID;
			if (!cognitoUrl || !clientId) {
				return null;
			}

			// const state = btoa(
			// 	JSON.stringify({
			// 		redirectPath,
			// 		timestamp: Date.now(),
			// 	})
			// );
			const redirectUri = `${window.location.origin}${AUTH_CALLBACK_PATH}`;
			const params = new URLSearchParams({
				client_id: clientId,
				response_type: 'code',
				redirect_uri: redirectUri,
				scope: AUTH_SCOPE,
			});

			return `${cognitoUrl}?${params.toString()}`;
		},
		[]
	);

	const clearAuthAndRedirect = useCallback(async () => {
		await cacheManager.clear();
		localStorage.clear();
		setisLogin(false);
		// const currentPath = location.pathname + location.search;
		const oauthRedirect = buildOauthRedirect();

		if (oauthRedirect) {
			window.location.assign(oauthRedirect);
		} else {
			navigate('/access-denied', { replace: true });
		}
	}, [buildOauthRedirect, navigate]);

	const authCheck = useCallback(async () => {
		const userId = localStorage.getItem(TOKEN_KEYS.USER_ID);

		if (!userId) {
			clearAuthAndRedirect();
			return;
		}

		try {
			const userDetailsResponse = await fetchBaseQueryWithEncryption(
				`${import.meta.env.VITE_COMPANY_API_URL}/user-mgmt/details-user/${userId}`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			);

			const userProfile =
				userDetailsResponse?.data?.decryptedData ?? userDetailsResponse?.data?.localData;

			if (!userProfile) {
				throw new Error('Missing user profile');
			}

			dispatch(setCurrentUser(userProfile));
			dispatch(setUserProfile({ userProfile }));
			localStorage.setItem('currentUser', JSON.stringify(userProfile));

			if (userProfile.companyId) {
				localStorage.setItem('compId', userProfile.companyId);
			}
			if (userProfile.lastVisitedTeam) {
				localStorage.setItem('lastVisitedTeam', userProfile.lastVisitedTeam);
			}
			if (userProfile.lastVisitedProject) {
				localStorage.setItem('lastVisitedProject', userProfile.lastVisitedProject);
			}

			const roleCodeFromProfile =
				userProfile?.invitedCompanies?.[0]?.roleCode || localStorage.getItem(TOKEN_KEYS.ROLE);

			if (!roleCodeFromProfile) {
				throw new Error('Missing role code');
			}

			localStorage.setItem(TOKEN_KEYS.ROLE, roleCodeFromProfile);

			const roleDetailsResponse = await dispatch(
				roleAuthMgmt.endpoints.getRoleCodeDetails.initiate(roleCodeFromProfile)
			);

			if (!roleDetailsResponse?.data?.data?.decryptedData) {
				throw new Error('Failed to load role details');
			}

			applyRolePermissions(roleDetailsResponse?.data?.data?.decryptedData);
			setisLogin(true);
		} catch (error) {
			console.error('Protected route auth failed:', error);
			clearAuthAndRedirect();
		}
	}, [clearAuthAndRedirect, dispatch]);

	useEffect(() => {
		authCheck();
	}, [authCheck]);

	if (!isLogin) return <PhantomLoader />;

	return <>{children}</>;
};

export default ProtectedRoute;
